import { useCallback, useEffect, useState } from 'react';

import { Slider } from '@/components/ui/slider';
import { useVisualization } from '@/context/visualization-context';
import { cn } from '@/lib/utils';
import { CurrentLocationService } from '@/visualization/services/current-location-service';

export interface SliceSliderProps {
    viewportId: string;
}

export function SliceSlider({ viewportId }: SliceSliderProps) {
    const { getViewportService, getServiceManager } = useVisualization();

    const [sliceIndex, setSliceIndex] = useState<number>(0);

    useEffect(() => {
        const viewportService = getViewportService();
        const locationService = getServiceManager().getService<CurrentLocationService>(
            CurrentLocationService.SERVICE_NAME
        );

        // Sync initial value
        const info = viewportService.getSliceInfo(viewportId);
        if (info) setSliceIndex(info.sliceIndex);

        return locationService.subscribe(locationService.EVENTS.LOCATION_CHANGED, () => {
            const info = viewportService.getSliceInfo(viewportId);
            if (!info) return;
            setSliceIndex((prev) => (prev !== info.sliceIndex ? info.sliceIndex : prev));
        });
    }, [getServiceManager, getViewportService, viewportId]);

    const handleValueChange = useCallback(
        (values: number[]) => {
            if (values.length === 0) return;
            getViewportService().setSliceIndex(viewportId, values[0]);
        },
        [viewportId, getViewportService]
    );

    const info = getViewportService().getSliceInfo(viewportId);
    if (!info) return null;

    const maxSlice = info.sliceCount - 1;
    if (maxSlice < 0) return null;

    // Oblique viewports: invert the slider so that dragging down moves
    // distally along the vessel (higher index) to match scroll behavior.
    const displayValue = info.isOblique ? maxSlice - sliceIndex : sliceIndex;

    const handleSliderChange = (values: number[]) => {
        if (values.length === 0) return;
        const raw = values[0];
        handleValueChange([info.isOblique ? maxSlice - raw : raw]);
    };

    return (
        <div
            className={cn(
                'absolute right-1 top-2 bottom-2',
                'flex flex-col items-center gap-1',
                'pointer-events-auto'
            )}
        >
            <div className="flex flex-col items-center gap-1 rounded-md shadow-sm px-1 py-2 h-full opacity-50 hover:opacity-100 transition-opacity duration-300">
                <Slider
                    orientation="vertical"
                    className={cn(
                        'flex-1 min-h-0',
                        '**:data-[slot=slider-range]:bg-transparent',
                        '**:data-[slot=slider-track]:w-1',
                        '**:data-[slot=slider-thumb]:size-3'
                    )}
                    min={0}
                    max={maxSlice}
                    value={[displayValue]}
                    onValueChange={handleSliderChange}
                    dir={info.isOblique ? 'ltr' : 'rtl'}
                />
            </div>
        </div>
    );
}
